import { buildContactCardContact } from '@poseidon/shared-utils';

import { cn } from '../cn';
import ContactCardBodyPure from '../ContactCardBodyPure';
import type { Stage } from '../ContactCardBodyPure';
import { ContactCardContainer } from '../ContactCardContainer';
import { ContactCardImageWithOwner } from '../ContactCardImage';
import { contactNameFlat } from '../contactUtils';

import type { OrgContactItem } from './types';

type OrgContactCardPureProps = {
  item: OrgContactItem;
  stages: Stage[];
  stagesLoading?: boolean;
  stageUpdating: boolean;
  isActive: boolean;
  isChecked: boolean;
  onClick: () => void;
  onStageChange: (stageId: string | null) => void;
};

/**
 * Package-local equivalent of the app-local ContactCardPure, specialized for the
 * org interact tab. ContactCardPure can't be imported here (it lives in the
 * dashboard and pulls in app-only deps), so this composes the same shared
 * primitives and reuses the same `buildContactCardContact` normalizer, keeping
 * the card visually identical while staying inside packages/components.
 */
export const OrgContactCardPure = ({
  item,
  stages,
  stagesLoading,
  stageUpdating,
  isActive,
  isChecked,
  onClick,
  onStageChange,
}: OrgContactCardPureProps) => {
  const contactUser = item.activeCadence?.cadenceUser ?? item.authedUser;
  const cadence = item.activeCadence?.cadence;

  const contact = buildContactCardContact({
    id: item.contact.id,
    firstName: item.contact.primaryFields.firstName?.data,
    lastName: item.contact.primaryFields.lastName?.data,
    photoUrl: item.contact.primaryPictureUrl,
    phone: item.contact.primaryFields.phone?.data,
    phoneVerified: item.contact.primaryFields.phone?.verified,
    professionalEmail: item.contact.primaryFields.professionalEmail?.data,
    professionalEmailVerified: item.contact.primaryFields.professionalEmail?.verified,
    title: item.contact.primaryFields.title?.data,
    stageId: item.contact.stageId,
    honorificTitle: item.contact.primaryFields.honorificTitle?.data,
    displayHonorific: item.contact.displayHonorific ?? false,
    liOpenLink: item.contact.liOpenLink,
    triggeredSignals: item.contact.triggeredSignals ?? null,
    signalsComputed: item.contact.signalsComputed ?? false,
  });

  const name = contactNameFlat({ first_name: contact.firstName ?? null, last_name: contact.lastName ?? null });

  return (
    <div className={cn('group flex gap-3.5')}>
      <ContactCardContainer isChecked={isChecked} active={isActive} onClick={onClick}>
        <ContactCardImageWithOwner
          name={name}
          firstName={contact.firstName}
          lastName={contact.lastName}
          imageSrc={contact.photoUrl}
          cutoutSrc={contact.cutoutUrl}
          cadenceName={cadence?.name}
          showContactUser={contactUser != null}
          contactUserProfilePicture={contactUser?.profilePicture}
          contactUserLiConnected={contactUser?.liConnected}
          contactUserLiConnectedAt={contactUser?.liConnectedAt}
          contactUserName={contactUser?.name}
          contactUserEmail={contactUser?.email}
          signalsComputed={contact.signalsComputed}
          triggeredSignals={contact.triggeredSignals}
        />
        <ContactCardBodyPure
          name={name}
          honorificTitle={contact.displayHonorific ? (contact.honorificTitle ?? undefined) : undefined}
          liOpenLink={contact.liOpenLink}
          liConnected={contactUser?.liConnected}
          disqualified={contactUser?.disqualified}
          title={contact.title}
          triggeredSignals={contact.triggeredSignals}
          hasResponse={false}
          cadenceName={cadence?.name}
          cadenceIsActive={cadence?.isActive}
          cadenceState={cadence?.state}
          cadenceRunning={cadence?.running}
          cadenceNextStepTypes={cadence?.nextStepTypes ?? undefined}
          cadenceInterruptMessage={cadence?.interruptMessage}
          canNotRunStepTypes={undefined}
          cadenceSteps={null}
          stages={stages}
          stageId={contact.stageId}
          stagesLoading={stagesLoading}
          stageUpdating={stageUpdating}
          onStageChange={onStageChange}
          phone={contact.phone}
          phoneVerified={contact.phoneVerified}
          professionalEmail={contact.professionalEmail}
          professionalEmailVerified={contact.professionalEmailVerified}
        />
      </ContactCardContainer>
    </div>
  );
};
